import React, { useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";

const Container = styled.section`
  margin: 0 auto;
  padding: 4rem 1rem;
  font-family: "Manrope", sans-serif;
`;

const Title = styled(motion.h2)`
  font-size: 2rem;
  color: #102a8e;
  text-align: center;
  margin-bottom: 4rem;
  font-weight: bold;
`;

const CarouselContainer = styled.div`
  position: relative;
  margin: 0 auto;
  max-width: 1400px;
  overflow: hidden;
`;

const QuotesWrapper = styled(motion.div)`
  display: flex;
  gap: 2rem;
  padding: 0 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const QuoteCard = styled(motion.div)`
  background: #fdfdfd;
  border-radius: 12px;
  padding: 1rem;
  box-shadow: 0px 4px 10px 0px #0000000d;
  position: relative;
  flex: 1;

  @media (max-width: 768px) {
    min-width: 100%;
  }
`;

const BlueCircle = styled.div`
  border-radius: 50%;
  position: absolute;
  top: 0rem;
  right: 0rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;

  svg {
    width: 46px;
    height: 46px;
    stroke: currentColor;
  }
`;

const Stars = styled.div`
  color: #ffb800;
  margin-bottom: 1rem;
  display: flex;
  gap: 4px;
`;

const QuoteText = styled.p`
  color: #666;
  line-height: 1.6;
  font-size: 14px;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const UserAvatar = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
`;

const UserName = styled.p`
  font-weight: 500;
  color: #333;
  font-size: 14px;
`;

const DotIndicators = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 2rem;
`;

const Dot = styled.button<{ active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background: ${(props) => (props.active ? "#5575f7" : "#E0E0E0")};
  cursor: pointer;
  padding: 0;
  transition: background 0.3s ease;
`;

const Star = () => (
  <svg
    width="16"
    height="15"
    viewBox="0 0 16 15"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6.7156 0.92705C7.01495 0.00573921 8.31836 0.00573969 8.61771 0.92705L9.68728 4.21885C9.82116 4.63087 10.2051 4.90983 10.6383 4.90983H14.0995C15.0683 4.90983 15.471 6.14945 14.6873 6.71885L11.8872 8.75329C11.5367 9.00793 11.39 9.4593 11.5239 9.87132L12.5935 13.1631C12.8928 14.0844 11.8383 14.8506 11.0546 14.2812L8.25444 12.2467C7.90395 11.9921 7.42936 11.9921 7.07887 12.2467L4.2787 14.2812C3.49499 14.8506 2.44051 14.0844 2.73986 13.1631L3.80943 9.87132C3.9433 9.4593 3.79665 9.00793 3.44616 8.75329L0.645989 6.71885C-0.137724 6.14945 0.265051 4.90983 1.23377 4.90983H4.69497C5.1282 4.90983 5.51216 4.63087 5.64603 4.21885L6.7156 0.92705Z"
      fill="#FFBD2E"
    />
  </svg>
);

export const Quote: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(0);

  const testimonials = [
    {
      id: 1,
      text: "The courses helped me understand UK workplace expectations and how these show up in interviews. It made my preparation feel grounded and relevant.",
      name: "Rohan Yogesh",
      avatar: "/assets/avatar.png",
      rating: 5,
    },
    {
      id: 2,
      text: "I gained clarity on professional communication and interview expectations in the UK, which boosted my confidence significantly.",
      name: "Carolina Montague",
      avatar: "/assets/avatar.png",
      rating: 5,
    },
    {
      id: 3,
      text: "The combination of UK-focused guidance and interview preparation helped me approach applications and interviews with more structure.",
      name: "Bright Onne",
      avatar: "/assets/avatar.png",
      rating: 5,
    },
  ];

  return (
    <Container>
      <Title
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        What Healthcare Professionals Say
      </Title>

      <CarouselContainer>
        <QuotesWrapper
          initial={{ x: 0 }}
          animate={{ x: -currentPage * 100 + "%" }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          {testimonials.map((testimonial) => (
            <QuoteCard key={testimonial.id}>
              <BlueCircle>
                <svg
                  width="52"
                  height="52"
                  viewBox="0 0 52 52"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="2.16666"
                    y="1.5"
                    width="48.3333"
                    height="48.3333"
                    rx="24.1667"
                    fill="#5575F7"
                  />
                  <rect
                    x="2.16666"
                    y="1.5"
                    width="48.3333"
                    height="48.3333"
                    rx="24.1667"
                    stroke="white"
                    stroke-width="3"
                  />
                  <path
                    opacity="0.4"
                    d="M26.3333 39.0002C33.6973 39.0002 39.6666 33.0308 39.6666 25.6668C39.6666 18.3028 33.6973 12.3335 26.3333 12.3335C18.9693 12.3335 13 18.3028 13 25.6668C13 33.0308 18.9693 39.0002 26.3333 39.0002Z"
                    fill="white"
                  />
                  <path
                    d="M31.412 19.292H29.4253C27.972 19.292 26.8387 20.4253 26.8387 21.8787V23.8653C26.8387 25.3187 27.972 26.452 29.4253 26.452H31.972C31.8653 28.8653 31.3053 29.2253 29.692 30.172C29.212 30.452 29.0653 31.0653 29.3453 31.5453C29.532 31.8653 29.8653 32.0387 30.212 32.0387C30.3853 32.0387 30.5587 31.9987 30.7187 31.9053C33.1053 30.492 33.9987 29.572 33.9987 25.4653V21.9053C33.9987 20.452 32.8387 19.292 31.412 19.292ZM23.24 19.292H21.2533C19.8 19.292 18.6667 20.4253 18.6667 21.8787V23.8653C18.6667 25.3187 19.8 26.452 21.2533 26.452H23.8C23.6933 28.8653 23.1333 29.2253 21.52 30.172C21.04 30.452 20.8933 31.0653 21.1733 31.5453C21.36 31.8653 21.6933 32.0387 22.04 32.0387C22.2133 32.0387 22.3867 31.9987 22.5467 31.9053C24.9333 30.492 25.8267 29.572 25.8267 25.4653V21.9053C25.8133 20.452 24.6533 19.292 23.24 19.292Z"
                    fill="white"
                  />
                </svg>
              </BlueCircle>
              <Stars>
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} />
                ))}
              </Stars>
              <QuoteText>{testimonial.text}</QuoteText>
              <UserInfo>
                <UserAvatar src={testimonial.avatar} alt={testimonial.name} />
                <UserName>{testimonial.name}</UserName>
              </UserInfo>
            </QuoteCard>
          ))}
        </QuotesWrapper>

        <DotIndicators>
          {Array.from({ length: Math.ceil(testimonials.length / 3) }).map(
            (_, index) => (
              <Dot
                key={index}
                active={currentPage === index}
                onClick={() => setCurrentPage(index)}
              />
            )
          )}
        </DotIndicators>
      </CarouselContainer>
    </Container>
  );
};
